import baseAPI from "@/api/base";
import { GetReport } from "@/dtos/Report.dto";
import {
  GET_ANALYTIC_REPORTS_URL,
  GET_CONTACT_MOVEMENTS_URL,
  GET_DASHBOARD_STATS_URL,
  GET_METRIC_DETAILS_URL,
  GET_CALL_CONNECT_RATE_URL,
  GET_EMAIL_METRICS_URL,
  GET_LINKEDIN_METRICS_URL,
  GET_MEETING_BOOKING_METRICS_URL,
  GET_OPPORTUNITY_DASHBOARD_URL,
  GET_STAGE_OPPORTUNITIES_URL,
  GET_LEAD_SOURCE_OPPORTUNITIES_URL,
  GET_PRODUCT_PERFORMANCE_URL,
  EXPORT_OPPORTUNITY_DASHBOARD_CSV_URL,
  SAVE_REPORT_VIEW_URL,
  GET_REPORT_VIEWS_URL,
  UPDATE_REPORT_VIEW_URL,
  DELETE_REPORT_VIEW_URL,
  AI_INSIGHTS_URL,
  AI_CHAT_CONVERSATIONS_URL,
} from "@/utils/urls";
import { AxiosResponse } from "axios";

class Report {
  static getDashboardStats = async (getReportDto: any): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_DASHBOARD_STATS_URL, {
      params: getReportDto,
    });
    return response;
  };

  static getAnalyticReports = async (getReportDto: any): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_ANALYTIC_REPORTS_URL, {
      params: getReportDto,
    });
    return response;
  };

  static getContactMovements = async (getReportDto: any): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_CONTACT_MOVEMENTS_URL, {
      params: getReportDto,
    });
    return response;
  };

  static getMetricDetails = async (params: {
    type: string;
    startDate?: string;
    endDate?: string;
    period?: string;
    bucketOptionId?: string;
    organizationId?: string;
    page?: number;
    limit?: number;
    callDispositionId?: string;
  }): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_METRIC_DETAILS_URL, {
      params,
    });
    return response;
  };

  static getCallConnectRate = async (params: {
    startDate?: string;
    endDate?: string;
    period?: string;
    bucketOptionId?: string;
    organizationId?: string;
    callDispositionId?: string;
  }): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_CALL_CONNECT_RATE_URL, {
      params,
    });
    return response;
  };

  static getEmailMetrics = async (params: {
    startDate?: string;
    endDate?: string;
    period?: string;
    bucketOptionId?: string;
    organizationId?: string;
  }): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_EMAIL_METRICS_URL, { params });
    return response;
  };

  static getLinkedInMetrics = async (params: {
    startDate?: string;
    endDate?: string;
    period?: string;
    bucketOptionId?: string;
    organizationId?: string;
  }): Promise<AxiosResponse> => {
    return baseAPI.get(GET_LINKEDIN_METRICS_URL, { params });
  };

  static getMeetingBookingMetrics = async (params: {
    startDate?: string;
    endDate?: string;
    period?: string;
    bucketOptionId?: string;
    organizationId?: string;
    callDispositionId?: string;
  }): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_MEETING_BOOKING_METRICS_URL, {
      params,
    });
    return response;
  };

  static getOpportunityDashboard = async (params: {
    organizationId: string;
    period?: string;
    startDate?: string;
    endDate?: string;
    status?: string;
    compareTo?: string;
    compareStartDate?: string;
    compareEndDate?: string;
    leadSourceIds?: string[];
    opportunityStageIds?: string[];
    dealTypeIds?: string[];
    ownerIds?: string[];
    minValue?: number;
    maxValue?: number;
    bucketOptionId?: string;
  }): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(GET_OPPORTUNITY_DASHBOARD_URL, params);
    return response;
  };

  static getStageOpportunities = async (params: {
    organizationId: string;
    stageId: string;
    stageName?: string;
    period?: string;
    startDate?: string;
    endDate?: string;
    status?: string;
    compareTo?: string;
    compareStartDate?: string;
    compareEndDate?: string;
    leadSourceIds?: string[];
    opportunityStageIds?: string[];
    dealTypeIds?: string[];
    ownerIds?: string[];
    minValue?: number;
    maxValue?: number;
    bucketOptionId?: string;
  }): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(GET_STAGE_OPPORTUNITIES_URL, params);
    return response;
  };

  static getLeadSourceOpportunities = async (params: {
    organizationId: string;
    leadSourceId: string;
    leadSourceName?: string;
    period?: string;
    startDate?: string;
    endDate?: string;
    status?: string;
    compareTo?: string;
    compareStartDate?: string;
    compareEndDate?: string;
    leadSourceIds?: string[];
    opportunityStageIds?: string[];
    dealTypeIds?: string[];
    ownerIds?: string[];
    minValue?: number;
    maxValue?: number;
    bucketOptionId?: string;
  }): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(GET_LEAD_SOURCE_OPPORTUNITIES_URL, params);
    return response;
  };

  static getProductPerformance = async (params: {
    organizationId: string;
    period?: string;
    startDate?: string;
    endDate?: string;
    status?: string;
    compareTo?: string;
    compareStartDate?: string;
    compareEndDate?: string;
    leadSourceIds?: string[];
    opportunityStageIds?: string[];
    dealTypeIds?: string[];
    ownerIds?: string[];
    minValue?: number;
    maxValue?: number;
    bucketOptionId?: string;
  }): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(GET_PRODUCT_PERFORMANCE_URL, params);
    return response;
  };

  static exportOpportunityDashboardCSV = async (dashboardData: any): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(
      EXPORT_OPPORTUNITY_DASHBOARD_CSV_URL,
      { dashboardData },
      {
        responseType: "blob",
      }
    );
    return response;
  };

  static saveReportView = async (params: {
    name: string;
    reportType: string;
    viewData: any;
  }): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(SAVE_REPORT_VIEW_URL, params);
    return response;
  };

  static getReportViews = async (params: { reportType: string }): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_REPORT_VIEWS_URL, { params });
    return response;
  };

  static updateReportView = async (
    id: string,
    params: { name?: string; viewData?: any }
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(`${UPDATE_REPORT_VIEW_URL}/${id}`, params);
    return response;
  };

  static deleteReportView = async (id: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.delete(`${DELETE_REPORT_VIEW_URL}/${id}`);
    return response;
  };

  static getLatestAIInsights = async (params: {
    organizationId: string;
    bucketOptionId?: string;
    period?: string;
    startDate?: string;
    endDate?: string;
    createdById?: string;
  }): Promise<AxiosResponse> => {
    return baseAPI.get(AI_INSIGHTS_URL, { params });
  };

  static generateAIInsights = async (params: {
    organizationId: string;
    bucketOptionId?: string;
    period?: string;
    startDate?: string;
    endDate?: string;
    createdById?: string;
  }): Promise<AxiosResponse> => {
    return baseAPI.post(AI_INSIGHTS_URL, params, { timeout: 270_000 });
  };

  static createAIChatConversation = async (params: {
    channel: string;
    month: string;
  }): Promise<AxiosResponse> => {
    return baseAPI.post(AI_CHAT_CONVERSATIONS_URL, params);
  };

  static listAIChatConversations = async (params: {
    channel?: string;
    month?: string;
  }): Promise<AxiosResponse> => {
    return baseAPI.get(AI_CHAT_CONVERSATIONS_URL, { params });
  };

  static getAIChatConversation = async (id: string): Promise<AxiosResponse> => {
    return baseAPI.get(`${AI_CHAT_CONVERSATIONS_URL}/${id}`);
  };

  static sendAIChatMessage = async (
    conversationId: string,
    params: { content?: string; quickInsightCategory?: string },
    signal?: AbortSignal
  ): Promise<AxiosResponse> => {
    return baseAPI.post(`${AI_CHAT_CONVERSATIONS_URL}/${conversationId}/messages`, params, {
      timeout: 130_000,
      signal,
    });
  };

  static deleteAIChatConversation = async (id: string): Promise<AxiosResponse> => {
    return baseAPI.delete(`${AI_CHAT_CONVERSATIONS_URL}/${id}`);
  };
}

export default Report;
